/** Handle API calls across the application **/
import { Amplify } from "aws-amplify";
import { Hub as amplifyHub } from "aws-amplify/utils";
import {
	get as amplifyGet,
	post as amplifyPost,
	put as amplifyPut,
	patch as amplifyPatch,
	del as amplifyDel,
} from "aws-amplify/api";
import {
	getCurrentUser,
	fetchAuthSession,
	fetchUserAttributes,
} from "aws-amplify/auth";
import Constants from "./constants";

const CGT = Constants.COGNITO;
const RAPAPI = Constants.RAPAPI;
const ACCAPI_LOCAL_ENDPOINT = "http://localhost:8000";

// Session Id
const maximum = 99999;
const minimum = 10000;
const sessionId = Math.floor(Math.random() * (maximum - minimum + 1)) + minimum;

// Checks and automatically refreshes the auth session tokens
const getTokens = async () => {
	try {
		await fetchAuthSession();
	} catch (e) {
		console.error("Failed to get session:", e);
	}
};

// Set up Hub listener BEFORE configuring Amplify to catch OAuth callback errors
amplifyHub.listen("auth", (data) => {
	const { event, message } = data.payload;

	if (
		event === "signIn_failure" ||
		event === "signIn_failuer" ||
		(message && message.includes && message.includes("invalid_grant")) ||
		(data.payload &&
			data.payload.data &&
			data.payload.data.error === "invalid_grant")
	) {
		sessionStorage.setItem(
			"oauth_error",
			JSON.stringify({
				event,
				message,
				timestamp: new Date().toISOString(),
			})
		);
	}

	if (event === "signedIn") {
		getTokens();
	}
});

Amplify.configure({
	Auth: {
		Cognito: {
			identityPoolId: CGT.IdentityPoolId,
			region: CGT.Region,
			userPoolId: CGT.UserPoolId,
			userPoolClientId: CGT.ClientId,
			loginWith: {
				oauth: {
					domain: CGT.OAuth.Domain,
					scopes: CGT.OAuth.Scopes,
					redirectSignIn: ["http://localhost:8080/", CGT.OAuth.RedirectSignIn],
					redirectSignOut: [
						"http://localhost:8080/",
						CGT.OAuth.RedirectSignOut,
					],
					responseType: "code",
					socialProviders: CGT.OAuth.Providers,
				},
			},
		},
	},
	API: {
		REST: {
			[CGT.EndPoint.Name]: {
				endpoint: CGT.EndPoint.Url,
				region: CGT.Region,
			},
			[CGT.Website.Name]: {
				endpoint: CGT.Website.Url,
				region: CGT.Region,
			},
			[CGT.Account.Name]: {
				endpoint: CGT.Account.Url,
				region: CGT.Region,
			},
			[CGT.Concierge.Name]: {
				endpoint: CGT.Concierge.Url,
				region: CGT.Region,
			},
			[CGT.Reports.Name]: {
				endpoint: CGT.Reports.Url,
				region: CGT.Region,
			},
			[CGT.SmartPricing.Name]: {
				endpoint: CGT.SmartPricing.Url,
				region: CGT.Region,
			},
			[CGT.BookingPal.Name]: {
				endpoint: CGT.BookingPal.Url,
				region: CGT.Region,
			},
			[CGT.CommHub.Name]: {
				endpoint: CGT.CommHub.Url,
				region: CGT.Region,
			},
			[CGT.GuestBenefits.Name]: {
				endpoint: CGT.GuestBenefits.Url,
				region: CGT.Region,
			},
			[CGT.KnowledgeBot.Name]: {
				endpoint: CGT.KnowledgeBot.Url,
				region: CGT.Region,
			},
			[CGT.PropertyOperationManagement.Name]: {
				endpoint: CGT.PropertyOperationManagement.Url,
				region: CGT.Region,
			},
			...(CGT.IntegrationTools
				? {
						[CGT.IntegrationTools.Name]: {
							endpoint: CGT.IntegrationTools.Url,
							region: CGT.Region,
						},
				  }
				: {}),
		},
	},
}, {
	API: {
		REST: {
			retryStrategy: { strategy: "no-retry" },
		},
	},
});

let ApiHandler = () => {
	const normalizeError = (err) => {
		const status =
			err?.response?.statusCode ||
			err?.response?.status ||
			err?.statusCode ||
			err?.status;
		// aws-amplify's RestApiError only exposes response.body (a raw string),
		// not response.data, so the JSON error body has to be parsed here.
		let data = err?.response?.data;
		if (data === undefined && typeof err?.response?.body === "string") {
			try {
				data = JSON.parse(err.response.body);
			} catch (e) {
				data = err.response.body;
			}
		}
		const message =
			data?.Message || data?.message || err?.message || "Request failed";
		return Object.assign(err || {}, { status, data, message });
	};
	/**
	 * For get current authenticated user
	 * @return {user} Will return current user object
	 */
	let getAuthenticatedUser = async () => {
		let user = await getCurrentUser();
		return user;
	};
	/**
	 * For get current user info
	 * @return {userInfo} Will return user info object
	 */
	let getUserInfo = async () => {
		let user = await fetchUserAttributes();
		return user;
	};

	/**
	 * A one generic function for get API call.
	 * @param {string|object} path relative path of API or options object with apiName, path and options.
	 * @param {object} options object key apiName to set different API URL.
	 * @return A promise that resolves to an object with JSON data, if successful.
	 */
	let get = async (path, options) => {
		// Handle object parameter pattern
		if (typeof path === "object" && path !== null) {
			options = path.options || {};
			const apiName = path.apiName != null ? path.apiName : RAPAPI.NAME;
			path = path.path;

			if (!path) {
				return;
			}

			let response;
			try {
				const restOperation = amplifyGet({
					apiName: apiName,
					path: path,
					options,
				});

				const restResponse = await restOperation.response;

				// Response handling for blob/text/json with safe empty-body handling
				if (options && options.responseType === "blob") {
					const blobData = await restResponse.body.blob();
					response = { data: blobData, headers: restResponse.headers };
				} else if (options && options.responseType === "text") {
					const textData = await restResponse.body.text();
					response = { data: textData, headers: restResponse.headers };
				} else {
					const text = await restResponse.body.text();
					let data = null;
					if (text && String(text).trim().length) {
						try {
							data = JSON.parse(text);
						} catch (e) {
							console.log(
								"Warning: Failed to parse JSON response; returning raw text",
								e
							);
							data = text;
						}
					}
					response = { data, headers: restResponse.headers };
				}
			} catch (err) {
				console.log("Error while GET call:", err);
				throw normalizeError(err);
			}
			return response;
		}

		// Original implementation for separate parameters
		if (!path) {
			return;
		}

		options = options || {};
		let response;
		try {
			let apiName = options.apiName != null ? options.apiName : RAPAPI.NAME;
			const restOperation = amplifyGet({
				apiName: apiName,
				path: path,
				options,
			});

			const restResponse = await restOperation.response;

			if (options && options.responseType === "blob") {
				const blobData = await restResponse.body.blob();
				response = { data: blobData, headers: restResponse.headers };
			} else if (options && options.responseType === "text") {
				const textData = await restResponse.body.text();
				response = { data: textData, headers: restResponse.headers };
			} else {
				const text = await restResponse.body.text();
				let data = null;
				if (text && String(text).trim().length) {
					try {
						data = JSON.parse(text);
					} catch (e) {
						console.log(
							"Warning: Failed to parse JSON response; returning raw text",
							e
						);
						data = text;
					}
				}
				response = { data, headers: restResponse.headers };
			}
		} catch (err) {
			if (!options.silent) {
				console.log("Error while GET call:", err);
			}
			throw normalizeError(err);
		}
		return response;
	};

	/**
	 * For get logged in user’s permissions.
	 * @return {userInfo} Returns the logged in user’s permissions.
	 */
	let getPermissions = async () => {
		let data = await get(RAPAPI.PERMISSIONS, { isCognito: true });
		return data;
	};

	/**
	 * For fetch a properties.
	 * @param {object} opts keys page, limit, tid, sortField, sortOrder, keyword, org.
	 * @return {total, data} total will content total number of properties and data will contain current fetched properties.
	 */
	let getProperties = async (opts) => {
		let {
			page,
			limit,
			tid,
			sortField,
			sortOrder,
			keyword,
			org,
			allOrganizations,
			showAllOrg,
			fl,
		} = opts;
		let offset = page;
		let session = tid || sessionId;
		sortField = sortField || "property_id";
		sortOrder = sortOrder || "+";
		keyword = keyword ? `${encodeURIComponent(keyword)}` : "";
		org = org
			? `organization${showAllOrg ? "s" : ""}=${
					showAllOrg ? allOrganizations.join() : org
			  }&`
			: "";
		let newPage;
		if (window.location.href.split("?page=")[1]) {
			newPage = window.location.href.split("?page=")[1];
		} else {
			newPage = 0;
		}
		let filter;
		if (fl && fl.length) {
			filter = fl.join(",");
		}
		let newOffset = Number.isNaN(limit * offset)
			? (newPage - 1 > 0 ? newPage - 1 : 0) * limit
			: limit * offset;
		let params = `?${org}limit=${limit}&tid=${session}&offset=${newOffset}&sort=${sortField}${encodeURIComponent(
			sortOrder
		)}&keyword=${keyword}${filter ? `&fl=${filter}` : ""}`;
		try {
			let data = await get(RAPAPI.PROPERTIES + params, { isCognito: true });
			return {
				total: data.headers["x-total-count"],
				data: data.data,
			};
		} catch (e) {
			console.log(e, "error getProperties");
			return {
				total: 0,
				data: [],
			};
		}
	};
	/**
	 * For fetch a properties.
	 * @return {total, data} total will content total number of properties and data will contain current fetched properties.
	 */
	let getListings = async () => {
		let data = await get(RAPAPI.LISTINGS);
		return data;
	};

	/**
	 * For make custom API call.
	 * @param {string|object} type Enum post, put, patch, delete, or options object with apiName, path and options.
	 * @param {string} path relative path for URL.
	 * @param {object} pObj object for add body part.
	 * @return {object} return response from API.
	 */
	let update = async (type, path, pObj) => {
		// Handle object parameter pattern
		if (typeof type === "object" && type !== null) {
			const method = type.method || "post";
			pObj = type.options || {};
			const apiName = type.apiName != null ? type.apiName : RAPAPI.NAME;
			path = type.path;

			if (!path) {
				return;
			}

			let response;
			try {
				let restOperation;
				switch (method) {
					case "post":
						restOperation = amplifyPost({
							apiName: apiName,
							path: path,
							options: pObj,
						});
						break;
					case "put":
						restOperation = amplifyPut({
							apiName: apiName,
							path: path,
							options: pObj,
						});
						break;
					case "patch":
						restOperation = amplifyPatch({
							apiName: apiName,
							path: path,
							options: pObj,
						});
						break;
					case "delete":
						restOperation = amplifyDel({
							apiName: apiName,
							path: path,
							options: pObj,
						});
						break;
				}

				const restResponse = await restOperation.response;

				if (pObj && pObj.responseType === "blob") {
					const blobData = await restResponse.body.blob();
					response = { data: blobData, headers: restResponse.headers };
				} else if (pObj && pObj.responseType === "text") {
					const textData = await restResponse.body.text();
					response = { data: textData, headers: restResponse.headers };
				} else {
					const text = await restResponse.body.text();
					let data = null;
					if (text && String(text).trim().length) {
						try {
							data = JSON.parse(text);
						} catch (e) {
							console.log(
								"Warning: Failed to parse JSON response; returning raw text",
								e
							);
							data = text;
						}
					}
					response = { data, headers: restResponse.headers };
				}
			} catch (err) {
				console.log("Error while " + method + " call: ", err);
				throw normalizeError(err);
			}
			return response;
		}

		// Original implementation for separate parameters
		if (!path) {
			return;
		}
		let response;
		type = type || "post";
		try {
			let restOperation;
			switch (type) {
				case "post":
					restOperation = amplifyPost({
						apiName: RAPAPI.NAME,
						path: path,
						options: pObj,
					});
					break;
				case "put":
					restOperation = amplifyPut({
						apiName: RAPAPI.NAME,
						path: path,
						options: pObj,
					});
					break;
				case "patch":
					restOperation = amplifyPatch({
						apiName: RAPAPI.NAME,
						path: path,
						options: pObj,
					});
					break;
				case "delete":
					restOperation = amplifyDel({
						apiName: RAPAPI.NAME,
						path: path,
						options: pObj,
					});
					break;
			}

			const restResponse = await restOperation.response;

			if (pObj && pObj.responseType === "blob") {
				const blobData = await restResponse.body.blob();
				response = { data: blobData, headers: restResponse.headers };
			} else if (pObj && pObj.responseType === "text") {
				const textData = await restResponse.body.text();
				response = { data: textData, headers: restResponse.headers };
			} else {
				const text = await restResponse.body.text();
				let data = null;
				if (text && String(text).trim().length) {
					try {
						data = JSON.parse(text);
					} catch (e) {
						console.log(
							"Warning: Failed to parse JSON response; returning raw text",
							e
						);
						data = text;
					}
				}
				response = { data, headers: restResponse.headers };
			}
		} catch (err) {
			console.log("Error while " + type + " call: ", err);
			throw normalizeError(err);
		}
		return response;
	};

	/**
	 * For make POST API call.
	 * @param {string|object} path relative path for URL or options object with apiName, path and options.
	 * @param {object} pObj object for add body part.
	 * @return {object} return response from API.
	 */
	let post = async (path, pObj) => {
		// Handle object parameter pattern
		if (typeof path === "object" && path !== null) {
			// Special handling for blob responses
			if (path.options && path.options.responseType === "blob") {
				try {
					const restOperation = amplifyPost({
						apiName: path.apiName != null ? path.apiName : RAPAPI.NAME,
						path: path.path,
						options: path.options,
					});

					const restResponse = await restOperation.response;

					// Handle blob response differently than JSON response
					const blobData = await restResponse.body.blob();

					return {
						data: blobData,
						headers: restResponse.headers,
					};
				} catch (err) {
					console.log("Error while POST blob call:", err);
					throw normalizeError(err);
				}
			}

			// Normal JSON handling
			return await update({
				method: "post",
				apiName: path.apiName,
				path: path.path,
				options: path.options,
			});
		}
		// Original implementation
		return await update("post", path, pObj);
	};
	/**
	 * For make PUT API call.
	 * @param {string|object} path relative path for URL or options object with apiName, path and options.
	 * @param {object} pObj object for add body part.
	 * @return {object} return response from API.
	 */
	let put = async (path, pObj) => {
		// Handle object parameter pattern
		if (typeof path === "object" && path !== null) {
			return await update({
				method: "put",
				apiName: path.apiName,
				path: path.path,
				options: path.options,
			});
		}
		// Original implementation
		return await update("put", path, pObj);
	};
	/**
	 * For make patch API call.
	 * @param {string|object} path relative path for URL or options object with apiName, path and options.
	 * @param {object} pObj object for add body part.
	 * @return {object} return response from API.
	 */
	let patch = async (path, pObj) => {
		// Handle object parameter pattern
		if (typeof path === "object" && path !== null) {
			return await update({
				method: "patch",
				apiName: path.apiName,
				path: path.path,
				options: path.options,
			});
		}
		// Original implementation
		return await update("patch", path, pObj);
	};

	/**
	 * For make delete API call.
	 * @param {string|object} path relative path for URL or options object with apiName, path and options.
	 * @param {object} pObj object for add body part.
	 * @return {object} return response from API.
	 */
	let del = async (path, pObj) => {
		// Handle object parameter pattern
		if (typeof path === "object" && path !== null) {
			return await update({
				method: "delete",
				apiName: path.apiName,
				path: path.path,
				options: path.options,
			});
		}
		// Original implementation
		return await update("delete", path, pObj);
	};

	return {
		getAuthenticatedUser,
		getPermissions,
		getProperties,
		getListings,
		del,
		get,
		post,
		put,
		patch,
		update,
		getUserInfo,
	};
};

export default ApiHandler();
